import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DerivedSeries {
  /** Composite key: `${seriesName}|${authorName}` */
  key: string;
  seriesName: string;
  seriesNameDisplay: string;
  authorName: string;
  /** Distinct positions the user owns (any shelf) */
  positionsOwned: number[];
  /** Distinct positions the user has read */
  positionsRead: number[];
  /** Highest position read */
  highestPositionRead: number;
  /** Total books in series (from books.series_total), if known */
  totalKnown: number | null;
  /** All user_books in this series with their book data */
  books: SeriesBookEntry[];
  /** Whether the user has dismissed this series */
  isDismissed: boolean;
  /** Dismiss reason if any */
  dismissReason: string | null;
}

export interface SeriesBookEntry {
  userBookId: string;
  bookId: string;
  title: string;
  coverUrl: string | null;
  seriesPosition: number;
  shelf: string;
  finishedAt: string | null;
}

export function useSeriesProgress() {
  const queryClient = useQueryClient();

  const { data: allSeries = [], isLoading } = useQuery({
    queryKey: ['derived-series-progress'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // 1. Fetch all user books with series data, using explicit FK
      const { data: userBooks, error: booksError } = await supabase
        .from('user_books')
        .select(`
          id,
          shelf,
          finished_at,
          book_id,
          books!fk_user_books_book_id (
            id,
            title,
            cover_url,
            series_name,
            series_position,
            series_total,
            authors
          )
        `)
        .eq('user_id', user.id);

      if (booksError) throw booksError;
      if (!userBooks) return [];

      // 2. Fetch dismissed preferences
      const { data: prefs } = await supabase
        .from('user_series_tracking')
        .select('series_name, author_name, is_dismissed, dismiss_reason')
        .eq('user_id', user.id)
        .eq('is_dismissed', true);

      const dismissedMap = new Map(
        (prefs || []).map(p => [`${p.series_name}|${p.author_name.toLowerCase()}`, p])
      );

      // 3. Group by series + author, deduplicating by position
      const seriesMap = new Map<string, DerivedSeries>();

      for (const ub of userBooks) {
        const book = ub.books as any;
        if (!book?.series_name || !book?.series_position) continue;

        const normalizedSeries = book.series_name.toLowerCase().trim();
        const primaryAuthor = book.authors?.[0] || 'Unknown';
        const key = `${normalizedSeries}|${primaryAuthor.toLowerCase().trim()}`;

        if (!seriesMap.has(key)) {
          const dismissPref = dismissedMap.get(key);
          seriesMap.set(key, {
            key,
            seriesName: normalizedSeries,
            seriesNameDisplay: book.series_name,
            authorName: primaryAuthor,
            positionsOwned: [],
            positionsRead: [],
            highestPositionRead: 0,
            totalKnown: null,
            books: [],
            isDismissed: dismissPref?.is_dismissed || false,
            dismissReason: dismissPref?.dismiss_reason || null,
          });
        }

        const series = seriesMap.get(key)!;

        // Update total if available
        if (book.series_total && (!series.totalKnown || book.series_total > series.totalKnown)) {
          series.totalKnown = book.series_total;
        }

        // Deduplicate by position — only add if this position not already tracked
        const pos = book.series_position;
        if (!series.positionsOwned.includes(pos)) {
          series.positionsOwned.push(pos);

          series.books.push({
            userBookId: ub.id,
            bookId: book.id,
            title: book.title,
            coverUrl: book.cover_url,
            seriesPosition: pos,
            shelf: ub.shelf,
            finishedAt: ub.finished_at,
          });

          if (ub.shelf === 'read') {
            series.positionsRead.push(pos);
            if (pos > series.highestPositionRead) {
              series.highestPositionRead = pos;
            }
          }
        }
      }

      // Sort positions and books within each series
      for (const series of seriesMap.values()) {
        series.positionsOwned.sort((a, b) => a - b);
        series.positionsRead.sort((a, b) => a - b);
        series.books.sort((a, b) => a.seriesPosition - b.seriesPosition);
      }

      return Array.from(seriesMap.values());
    },
  });

  // Derived lists
  const activeSeries = allSeries.filter(s => !s.isDismissed);
  const dismissedSeries = allSeries.filter(s => s.isDismissed);
  const completedSeries = activeSeries.filter(
    s => s.totalKnown && s.positionsRead.length >= s.totalKnown
  );
  const inProgressSeries = activeSeries.filter(
    s => !s.totalKnown || s.positionsRead.length < s.totalKnown
  );

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: async ({ seriesName, authorName, reason }: { seriesName: string; authorName: string; reason?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_series_tracking')
        .upsert({
          user_id: user.id,
          series_name: seriesName,
          series_name_display: seriesName,
          author_name: authorName,
          highest_book_position: 0,
          is_dismissed: true,
          dismiss_reason: reason || null,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,series_name,author_name',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['derived-series-progress'] });
      toast.success('Series dismissed');
    },
    onError: () => toast.error('Failed to dismiss series'),
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: async ({ seriesName, authorName }: { seriesName: string; authorName: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_series_tracking')
        .update({
          is_dismissed: false,
          dismiss_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .eq('series_name', seriesName)
        .eq('author_name', authorName);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['derived-series-progress'] });
      toast.success('Series reactivated');
    },
    onError: () => toast.error('Failed to reactivate series'),
  });

  return {
    allSeries,
    activeSeries,
    dismissedSeries,
    completedSeries,
    inProgressSeries,
    isLoading,
    dismissSeries: dismissMutation.mutate,
    reactivateSeries: reactivateMutation.mutate,
  };
}
